import React, { useRef, useState } from 'react';
import classes from './Onboarding.module.css';
import OnboardingProgress from './OnboardingProgress';
import { FaCheck, FaDownload } from 'react-icons/fa';
import { IoCloseCircleOutline } from 'react-icons/io5';
import { RiFileExcel2Line } from 'react-icons/ri';
import { useAzurePublicUpload } from '../../hooks/useAzurePublicUpload';
import { useTransporterData } from '../../hooks/useTransporterData';
import { useCreateTransporterProfile } from '../../hooks/useCreateTransporterProfile';
import { Urls } from '../../Urls';
import { downloadFileToLocal } from '../../shared_utilities/fileDownloadUtils';
import axios from '../../Axios';
import { CircularProgress } from '../Shared/CircularProgress/CircularProgress';
import { useOnboardingMixpanelTrack } from './hooks/useOnboardingMixpanelTrack';

const STEPS = ['Download Template', 'Upload & Import'];

const validEmail = e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

const OnboardingExcelUpload = ({ onSuccess, onSwitchPath }) => {
    // ── Contact fields ────────────────────────────────────────
    const [pocName, setPocName] = useState('');
    const [pocEmail, setPocEmail] = useState('');
    const [errors, setErrors] = useState({});

    // ── File / upload state ───────────────────────────────────
    const [file, setFile] = useState(null);
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [successData, setSuccessData] = useState(null);
    const inputRef = useRef();

    const { data: transporterData, refetch } = useTransporterData();
    const { uploadFile } = useAzurePublicUpload();
    const { createProfile, saving } = useCreateTransporterProfile();
    const { trackOnboardingEvent } = useOnboardingMixpanelTrack();

    const isBusy = uploading || saving;

    /* ── drag handlers ──────────────────────────────────────── */
    const onDragOver = e => { e.preventDefault(); setDragging(true); };
    const onDragLeave = () => setDragging(false);
    const onDrop = e => {
        e.preventDefault();
        setDragging(false);
        const dropped = e.dataTransfer.files[0];
        if (dropped) handleFile(dropped);
    };

    const handleFile = f => {
        setError('');
        setSuccessData(null);
        const ext = f.name.split('.').pop().toLowerCase();
        if (!['xlsx', 'xls'].includes(ext)) {
            setError('Please upload an Excel file (.xlsx or .xls).');
            return;
        }
        setFile(f);
        trackOnboardingEvent(
            'onboarding excel',
            'Excel file selected for upload',
            { file_name: f.name }
        );
    };

    const removeFile = () => {
        trackOnboardingEvent(
            'onboarding excel',
            'Selected Excel file removed',
            { file_name: file?.name }
        );
        setFile(null);
        setError('');
        if (inputRef.current) inputRef.current.value = '';
    };

    /* ── contact validation ─────────────────────────────────── */
    const validateContact = () => {
        const e = {};
        if (!pocName.trim()) e.pocName = 'Full name is required';
        if (!validEmail(pocEmail)) e.pocEmail = 'Valid email address is required';
        setErrors(e);
        return !Object.keys(e).length;
    };

    /* ── template download (blank) ──────────────────────────── */
    const handleDownloadBlank = () => {
        trackOnboardingEvent(
            'onboarding excel',
            'Blank Excel template downloaded',
            { transporter_id: transporterData?.transporter_id }
        );
        downloadFileToLocal(Urls.excelTemplate);
    };

    /* ── pre-filled download ────────────────────────────────── */
    const handleDownloadPrefilled = async () => {
        try {
            const res = await axios.get(Urls.excelPrefilled, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `transporter_profile_${transporterData?.transporter_id || 'me'}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            setError('Could not download pre-filled template. Please try the blank template instead.');
        }
    };

    /* ── upload + save ──────────────────────────────────────── */
    const handleUpload = async () => {
        if (!validateContact()) return;
        if (!file) { setError('Please select a filled Excel file first.'); return; }

        setUploading(true);
        setError('');
        trackOnboardingEvent(
            'onboarding excel',
            'Excel profile import started',
            { file_name: file.name, transporter_id: transporterData?.transporter_id }
        );
        try {
            const customFilename = `${transporterData?.transporter_id || 'transporter'}/excel/${file.name}`;

            // Step 1 — upload xlsx to Azure
            const { blobUrl } = await uploadFile(file, customFilename);

            // Step 2 — send to Excel import API

            const _payload = {
                blob_url: blobUrl,
                name: pocName.trim(),
                email: pocEmail.trim(),
            }

            const response = await axios.post(Urls.excelUpload, _payload, { skipErrorToast: true });
            const data = response.data;

            if (data?.message !== 'Success') {
                const errMsg = data?.message || 'Upload succeeded but no data returned.';
                trackOnboardingEvent(
                    'onboarding excel',
                    'Excel profile import failed',
                    { error: errMsg, transporter_id: transporterData?.transporter_id }
                );
                setError(errMsg);
                setUploading(false);
                return;
            }

            trackOnboardingEvent(
                'onboarding excel',
                'Excel profile import completed successfully',
                {
                    transporter_id: transporterData?.transporter_id,
                    fields_updated_count: data.fields_updated?.length ?? 0,
                    percentage_completion: data.percentage_completion,
                }
            );
            setSuccessData(data);
            setUploading(false);

            // Step 3 — refetch: get fresh transporter data (now includes Excel-imported fields)
            // setUploading(false);
            // const refetchResult = await refetch();
            // const freshData = refetchResult?.data ?? transporterData ?? {};

            // // Step 4 — merge contact info and save
            // const payload = {
            //     ...freshData,
            //     contact_information: [
            //         {
            //             name: pocName.trim(),
            //             email: pocEmail.trim(),
            //             contact_number: freshData?.user_details?.contact_number ?? '',
            //             designation: '',
            //             poc: 'yes',
            //         },
            //     ],
            // };

            // const { success } = await createProfile(payload);
            // if (success) {
            //     setSuccessData(data);
            // } else {
            //     setError('Profile data imported but contact info could not be saved. Please update it from your profile page.');
            //     setSuccessData(data); // still show success — excel part worked
            // }
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.response?.data?.detail ||
                'Failed to process the Excel file. Please try again.';
            trackOnboardingEvent(
                'onboarding excel',
                'Excel profile import failed',
                { error: msg, transporter_id: transporterData?.transporter_id }
            );
            setError(msg);
            setUploading(false);
        }
    };

    /* ── success screen ─────────────────────────────────────── */
    if (successData) {
        return (
            <div className={classes.onboardingWrap}>
                <div className={classes.splitCard}>
                    {/* LEFT */}
                    <div className={classes.leftPanel}>
                        <div className={classes.logoWrap}>
                            <img src={Urls.LorriLogoLogin} alt="LoRRI" className={classes.logo} />
                        </div>
                        <p className={classes.subheading} style={{ marginBottom: 0 }}>Your profile is being set up</p>
                    </div>

                    {/* RIGHT */}
                    <div className={classes.rightPanel} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <div className={classes.excelSuccessBox}>
                            <div className={classes.excelSuccessIcon}>
                                <FaCheck />
                            </div>
                            <h2 className={classes.heading} style={{ marginTop: 16 }}>Profile Updated!</h2>
                            <p className={classes.subheading}>
                                Your Excel data has been imported successfully.
                            </p>

                            {/* Completion ring */}
                            <div className={classes.excelCompletionWrap}>
                                <div className={classes.excelCompletionRingOuter}>
                                    <CircularProgress percent={successData.percentage_completion} size={80} stroke={20} />
                                </div>
                                <p style={{ fontSize: 13, color: '#6e6b8a', marginTop: 8 }}>Profile Completion %</p>
                            </div>

                            {/* Fields updated */}
                            {Array.isArray(successData.fields_updated) && successData.fields_updated.length > 0 && (
                                <div className={classes.excelFieldsList}>
                                    <div style={{ fontSize: 12, color: '#9e9bb8', marginBottom: 6 }}>Fields imported:</div>
                                    <div className={classes.excelFieldTags}>
                                        {successData.fields_updated.map(f => (
                                            <span key={f} className={classes.excelFieldTag}>{f.replace(/_/g, ' ')}</span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {error && <div className={classes.uploadError} style={{ marginTop: 12 }}>{error}</div>}

                            <button
                                className={classes.btnPrimary}
                                style={{ marginTop: 24, width: '100%' }}
                                onClick={() => {
                                    trackOnboardingEvent(
                                        'onboarding excel',
                                        'User continued to profile after Excel import',
                                        { transporter_id: transporterData?.transporter_id }
                                    );
                                    onSuccess();
                                }}
                                id="excel-onboarding-continue"
                            >
                                Continue to My Profile →
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    /* ── main upload screen ─────────────────────────────────── */
    return (
        <div className={classes.onboardingWrap}>
            <div className={classes.splitCard}>

                {/* ═══ LEFT PANEL ═══ */}
                <div className={classes.leftPanel}>
                    <div className={classes.logoWrap}>
                        <img src={Urls.LorriLogoLogin} alt="LoRRI" className={classes.logo} />
                    </div>
                    <p className={classes.subheading} style={{ marginBottom: 0 }}>Fill at your own pace</p>
                    <div className={classes.excelStepInfoWrap}>
                        <div className={classes.excelStepInfo}>
                            <div className={classes.excelStepNum}>1</div>
                            <div><strong>Download</strong> the standard Excel template</div>
                        </div>
                        <div className={classes.excelStepInfo}>
                            <div className={classes.excelStepNum}>2</div>
                            <div><strong>Fill in</strong> your company details offline</div>
                        </div>
                        <div className={classes.excelStepInfo}>
                            <div className={classes.excelStepNum}>3</div>
                            <div><strong>Upload</strong> the filled file to import your profile</div>
                        </div>
                    </div>
                </div>

                {/* ═══ RIGHT PANEL ═══ */}
                <div className={classes.rightPanel} style={{ display: 'flex', flexDirection: 'column' }}>

                    <OnboardingProgress steps={STEPS} current={file || isBusy ? 1 : 0} />

                    <h1 className={classes.heading}>Excel Template Onboarding</h1>
                    <p className={classes.subheading} style={{ marginBottom: "0px" }}>
                        Download our standard template, fill in your details, then upload it back to auto-populate your profile.
                    </p>

                    {/* ── Download Buttons ── */}
                    <div className={classes.excelDownloadRow}>
                        <button
                            className={classes.excelDownloadBtn}
                            onClick={handleDownloadBlank}
                            id="excel-download-blank"
                            type="button"
                        >
                            <FaDownload style={{ marginRight: 8 }} />
                            Download Blank Template
                        </button>
                        {/* <button
                            className={`${classes.excelDownloadBtn} ${classes.excelDownloadBtnOutlined}`}
                            onClick={handleDownloadPrefilled}
                            id="excel-download-prefilled"
                            type="button"
                        >
                            <FaDownload style={{ marginRight: 8 }} />
                            Download Pre-filled Template
                        </button> */}
                    </div>

                    {/* ── Contact Details (required) ── */}
                    <div className={classes.excelContactSection}>
                        <div className={classes.excelContactLabel}>Your Contact Details</div>

                        <div className={classes.excelContactRow}>
                            <div className={classes.fieldWrap} style={{ flex: 1 }}>
                                <div className={classes.fieldLabel}>
                                    <span className={classes.asterisk}>*</span>Full Name
                                </div>
                                <input
                                    className={`${classes.textInput} ${errors.pocName ? classes.invalid : ''}`}
                                    placeholder="Contact person name"
                                    value={pocName}
                                    onChange={e => {
                                        setPocName(e.target.value);
                                        setErrors(p => ({ ...p, pocName: '' }));
                                    }}
                                    id="excel-poc-name"
                                    disabled={isBusy}
                                />
                                {errors.pocName && <div className={classes.errorMsg}>{errors.pocName}</div>}
                            </div>

                            <div className={classes.fieldWrap} style={{ flex: 1 }}>
                                <div className={classes.fieldLabel}>
                                    <span className={classes.asterisk}>*</span>Email Address
                                </div>
                                <input
                                    className={`${classes.textInput} ${errors.pocEmail ? classes.invalid : ''}`}
                                    placeholder=""
                                    type="email"
                                    value={pocEmail}
                                    onChange={e => {
                                        setPocEmail(e.target.value);
                                        setErrors(p => ({ ...p, pocEmail: '' }));
                                    }}
                                    id="excel-poc-email"
                                    disabled={isBusy}
                                />
                                {errors.pocEmail && <div className={classes.errorMsg}>{errors.pocEmail}</div>}
                            </div>
                        </div>
                    </div>

                    <div className={classes.excelDivider}>
                        <span>Then upload your filled Excel</span>
                    </div>

                    {/* ── Drop Zone / Loading ── */}
                    {isBusy ? (
                        <div className={classes.extractingWrap} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <div className={classes.extractingSpinner} />
                            <div className={classes.extractingText} style={{ marginBottom: 10 }}>
                                {uploading ? 'Processing your Excel…' : 'Saving contact details…'}
                            </div>
                            <p style={{ fontSize: '11px', color: '#b0aed4' }}>Please don't refresh the page.</p>
                        </div>
                    ) : (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', paddingRight: 4 }}>
                            <div
                                className={`${classes.dropZone} ${dragging ? classes.dragging : ''}`}
                                onDragOver={onDragOver}
                                onDragLeave={onDragLeave}
                                onDrop={onDrop}
                                onClick={() => !file && inputRef.current?.click()}
                                id="excel-drop-zone"
                                style={{ flex: 1, minHeight: 120 }}
                            >
                                <input
                                    ref={inputRef}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    style={{ display: 'none' }}
                                    onChange={e => e.target.files[0] && handleFile(e.target.files[0])}
                                />
                                {!file ? (
                                    <>
                                        <div className={classes.dropIcon} style={{ color: '#1d6f42' }}>
                                            <RiFileExcel2Line />
                                        </div>
                                        <div className={classes.dropTitle}>Drag & drop your filled Excel here</div>
                                        <div className={classes.dropSub}>or</div>
                                        <span className={classes.browseLink}>Browse files</span>
                                        <div className={classes.uploadFormats}>XLSX · XLS</div>
                                    </>
                                ) : (
                                    <div className={classes.fileChosen} onClick={e => e.stopPropagation()}>
                                        <RiFileExcel2Line className={classes.fileChosenIcon} style={{ color: '#1d6f42' }} />
                                        <div className={classes.fileChosenName}>{file.name}</div>
                                        <span className={classes.removeFile} onClick={removeFile} title="Remove file">
                                            <IoCloseCircleOutline />
                                        </span>
                                    </div>
                                )}
                            </div>

                            {error && <div className={classes.uploadError}>{error}</div>}

                            {/* ── Footer buttons ── */}
                            <div className={classes.reviewStickyFooter} style={{ borderTop: 'none', paddingTop: 16 }}>
                                <button
                                    className={classes.btnSecondary}
                                    onClick={onSwitchPath}
                                    id="excel-onboarding-back"
                                    disabled={isBusy}
                                >
                                    ← Back
                                </button>
                                <button
                                    className={classes.btnPrimary}
                                    onClick={handleUpload}
                                    disabled={!file || !pocName.trim() || !pocEmail.trim() || isBusy}
                                    id="excel-onboarding-submit"
                                >
                                    Import Profile →
                                </button>
                            </div>

                            <div className={classes.centerText} style={{ marginTop: 12 }}>
                                <button className={classes.linkBtn} onClick={onSwitchPath} disabled={isBusy}>
                                    Prefer to fill manually instead?
                                </button>
                            </div>
                        </div>
                    )}

                </div>{/* end rightPanel */}
            </div>
        </div>
    );
};

export default OnboardingExcelUpload;
